/**
 * <ls-size-chart> wraps the size chart pop-up body. It switches every chart between inches and cm and remembers the choice.
 * Each number is printed twice by the server ([data-unit-in] and [data-unit-cm][hidden]), so this only toggles `hidden` and aria-pressed.
 * The state also lives on the element and in localStorage, and a MutationObserver puts it back if a morph or section re-render resets the markup.
 */
const STORAGE_KEY = 'ls-size-unit';

/** @returns {'in' | 'cm'} */
function readSavedUnit() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'cm' ? 'cm' : 'in';
  } catch (_error) {
    return 'in';
  }
}

/** @param {'in' | 'cm'} unit */
function saveUnit(unit) {
  try {
    localStorage.setItem(STORAGE_KEY, unit);
  } catch (_error) {
    // Storage can be blocked (private window, site data off). The choice then lasts until the page reloads.
  }
}

class LsSizeChart extends HTMLElement {
  /** @type {'in' | 'cm'} */
  #unit = 'in';

  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#unit = readSavedUnit();
    this.addEventListener('click', this.#onClick);
    this.#apply();

    this.#observer = new MutationObserver(() => this.#apply());
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['hidden', 'aria-pressed'],
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-ls-unit]') : null;
    if (!button || !this.contains(button)) return;

    this.#unit = button.getAttribute('data-ls-unit') === 'cm' ? 'cm' : 'in';
    saveUnit(this.#unit);
    this.#apply();
  };

  /** Writes only when something differs, so the observer does not loop. */
  #apply() {
    const showCm = this.#unit === 'cm';

    for (const el of this.querySelectorAll('[data-unit-in]')) {
      if (/** @type {HTMLElement} */ (el).hidden !== showCm) /** @type {HTMLElement} */ (el).hidden = showCm;
    }
    for (const el of this.querySelectorAll('[data-unit-cm]')) {
      if (/** @type {HTMLElement} */ (el).hidden === showCm) /** @type {HTMLElement} */ (el).hidden = !showCm;
    }
    for (const button of this.querySelectorAll('[data-ls-unit]')) {
      const pressed = String(button.getAttribute('data-ls-unit') === this.#unit);
      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    }
  }
}

if (!customElements.get('ls-size-chart')) {
  customElements.define('ls-size-chart', LsSizeChart);
}
